import type { Notification } from '../types/notification.types';

/**
 * How loudly a transition deserves to interrupt someone.
 *
 * `alert` — something needs a decision. `success` — an outcome they were waiting for landed.
 * `info` — a genuinely new notification that is not a posting outcome at all.
 */
export type ArrivalTone = 'alert' | 'success' | 'info';

/** Loudest first. Used to pick ONE tone when a batch carries several transitions. */
const SEVERITY: readonly ArrivalTone[] = ['alert', 'success', 'info'];

/**
 * Should moving from `before` to `after` announce itself, and how?
 *
 * ⚠️ THIS IS KEYED ON THE TRANSITION, NOT ON "have I seen this id". That distinction is the whole
 * fix (owner, 2026-08-27). Our notification model is a JOURNEY, not a log — `INotificationEmitter`
 * writes one row per `(job, recipient)` and MORPHS it in place — so a posting's whole life is one
 * id: created at enqueue by `UpsertQueuedAsync`, morphed at settle by `UpsertPostingTerminalAsync`.
 *
 * Announcing on "an id I do not hold" therefore announced the ONE moment the user already knew
 * about (they had just clicked Post) and stayed silent on every outcome — including a FAILURE that
 * needs a decision from them. It was also timing-dependent: a tab that was disconnected at enqueue
 * first saw the row already-terminal, so the same posting announced or did not depending on the
 * connection.
 *
 * The rule instead: announce what carries news.
 *
 *   queued / posting / retrying   silent — self-inflicted, or pure churn. This is exactly what
 *                                 Android's `setOnlyAlertOnce` exists to suppress.
 *   posted                        success — the outcome, and they cannot see it coming
 *   failed / reconcile            alert — needs a decision; the old silence here was the real bug
 *   cancelled                     silent — someone asked for it
 *   anything not a posting        info
 */
export function toneForTransition(
  before: Notification | undefined,
  after: Notification,
): ArrivalTone | null {
  // Archived. Whatever it did, the user has already filed it away.
  if (after.done) return null;

  // A row we already hold whose journey has not moved. N9's reconciler repairs whole windows of
  // existing rows in one frame; without this, every repair would chime.
  if (before !== undefined && before.state === after.state) return null;

  // Needs-action outranks the state chip, exactly as `severityOf` in the row does. A failed posting
  // nobody has discharged is the most important thing in the list whatever its journey says.
  if (after.needsAction && !after.resolved) return 'alert';

  // ⚠️ `category` is an open vocabulary on the wire (`category: string`), so this is deliberately a
  // NEGATIVE test: anything that is not a posting gets the neutral tone rather than being silently
  // dropped by a switch that only knows posting states. A new category announces itself on day one.
  if (after.category !== 'posting') return before === undefined ? 'info' : null;

  switch (after.state) {
    case 'posted':
      return 'success';
    case 'failed':
    case 'reconcile':
      return 'alert';
    case 'queued':
    case 'posting':
    case 'retrying':
    case 'cancelled':
      return null;
    default:
      // An unknown state from a newer server. Announcing once beats silence for something we cannot
      // classify — and it is a transition, so it happens at most once per move.
      return 'info';
  }
}

/** The loudest of the two, so one batch of transitions produces one tone. */
export function louderTone(a: ArrivalTone | null, b: ArrivalTone | null): ArrivalTone | null {
  if (a === null) return b;
  if (b === null) return a;
  return SEVERITY.indexOf(a) <= SEVERITY.indexOf(b) ? a : b;
}
